import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import DynamicBanner from "../../Pages/include/DynamicBanner";
import api from "../../../api";
import NotFound from "../../Pages/NotFound/NotFound";

const BlogDetails = () => {
  const { slug } = useParams();

  const [blog, setBlog] = useState(null);
  const [loadingBlog, setLoadingBlog] = useState(true);
  const [blogError, setBlogError] = useState("");
  const [notFound, setNotFound] = useState(false);

  const [allBlogs, setAllBlogs] = useState([]);
  const [loadingAll, setLoadingAll] = useState(true);

  const resolveImage = (img) => {
    if (!img) return "";
    if (img.startsWith("http://") || img.startsWith("https://")) return img;
    if (img.startsWith("/")) return `${process.env.REACT_APP_API_URL || ""}/public${img}`;
    return img;
  };

  useEffect(() => {
    let cancelled = false;
    const fetchBlog = async () => {
      setLoadingBlog(true);
      setBlogError("");
      try {
        const res = await api.get(`/api/blog/${slug}`);
        const data = res?.data?.data ?? null;
        if (!cancelled) setBlog(data);
        setNotFound(false);
      } catch (err) {
        console.error("Error fetching blog by slug:", err);
        setNotFound(true);
        if (!cancelled) setBlogError("Failed to load blog.");
      } finally {
        if (!cancelled) setLoadingBlog(false);
      }
    };

    if (slug) fetchBlog();
    return () => { cancelled = true; };
  }, [slug]);

  useEffect(() => {
    let cancelled = false;
    const fetchAllBlogs = async () => {
      setLoadingAll(true);
      try {
        const res = await api.get("/api/blog");
        const data = res?.data?.data ?? [];
        const normalized = (Array.isArray(data) ? data : []).map((b) => {
          const img = b.blog_image || b.image || "";
          return {
            ...b,
            resolved_image: img ? resolveImage(img) : "",
          };
        });
        if (!cancelled) setAllBlogs(normalized);
      } catch (err) {
        console.error("Error fetching all blogs for sidebar:", err);
        if (!cancelled) setAllBlogs([]);
      } finally {
        if (!cancelled) setLoadingAll(false);
      }
    };

    fetchAllBlogs();
    return () => { cancelled = true; };
  }, []);

  const bannerProps = useMemo(() => {
    if (!blog) {
      return { title: "Blog Details", bgImage: [], bgImageMobile: [] };
    }

    // Fix: Resolve images from the banner array provided in API
    const desktopImages = Array.isArray(blog.banner?.desktop_images) 
      ? blog.banner.desktop_images 
      : [];
      
    const mobileImages = Array.isArray(blog.banner?.mobile_images) 
      ? blog.banner.mobile_images 
      : desktopImages;

    return {
      title: blog.banner?.heading || blog.title || "Blog Details",
      bgImage: desktopImages,
      bgImageMobile: mobileImages,
    };
  }, [blog]);

  const sidebarBlogs = useMemo(() => {
    if (!Array.isArray(allBlogs)) return [];
    return allBlogs
      .filter((b) => b && b.slug && b.slug !== slug)
      .map((b) => ({
        id: b.id,
        title: b.title,
        date: b.published_date
          ? new Date(b.published_date).toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
          })
          : "",
        image: b.resolved_image || "",
        slug: b.slug,
      }));
  }, [allBlogs, slug]);

  const shareLink = blog?.share_link || (typeof window !== "undefined" ? window.location.href : "");
  const shareFacebook = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareLink)}`;
  const shareWhatsapp = `https://api.whatsapp.com/send?text=${encodeURIComponent(blog?.title ? `${blog.title} - ${shareLink}` : shareLink)}`;
  const shareInstagram = `https://www.instagram.com/?url=${encodeURIComponent(shareLink)}`;
  const shareTwitter = `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareLink)}&text=${encodeURIComponent(blog?.title || "")}`;
  const shareLinkedin = `https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(shareLink)}`;

  const formatDate = (d) => {
    if (!d) return "";
    try {
      return new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
    } catch { return d; }
  };

  if (notFound) return <NotFound />;
  if (blogError || (!blog && !loadingBlog)) return <NotFound />;

  if (loadingBlog) {
    return (
      <>
        <DynamicBanner title="Loading..." bgImage={[]} bgImageMobile={[]} />
        <section className="blog-details-section">
          <div className="container text-center py-5">Loading blog...</div>
        </section>
      </>
    );
  }

  return (
    <>
      <DynamicBanner {...bannerProps} />

      <section className="blog-details-section">
        <div className="container blog-details-container">
          <div className="blog-details-content">
            <div className="blog-main-title text-center pt-3">
              <h2>{blog.title}</h2>
            </div>

            {blog.section_1?.image && (
              <img
                src={resolveImage(blog.section_1.image)}
                alt={blog.section_1.heading || blog.title}
                className="blog-details-image"
              />
            )}

            <div className="share-inline-box">
              <h4>Share This Post<span className="blog-date ms-3">{formatDate(blog.published_date)}</span></h4>
              <div className="share-inline-icons">
                <a href={shareFacebook} className="facebook" target="_blank" rel="noopener noreferrer"><i className="fa fa-facebook"></i></a>
                <a href={shareWhatsapp} className="whatsapp" target="_blank" rel="noopener noreferrer"><i className="fa fa-whatsapp"></i></a>
                <a href={shareInstagram} className="instagram" target="_blank" rel="noopener noreferrer"><i className="fa fa-instagram"></i></a>
                <a href={shareTwitter} className="twitter" target="_blank" rel="noopener noreferrer"><i className="fa fa-twitter"></i></a>
                <a href={shareLinkedin} className="linkedin" target="_blank" rel="noopener noreferrer"><i className="fa fa-linkedin"></i></a>
              </div>
            </div>

            <div className="blog-details-text">
              {Array.isArray(blog.section_2) &&
                blog.section_2.map((sec, idx) => (
                  <div key={idx} className="mb-4">
                    {sec.heading && <h4>{sec.heading}</h4>}
                    {sec.content && <p>{sec.content}</p>}
                    {Array.isArray(sec.list) && sec.list.length > 0 && (
                      <ul className="list-icon">
                        {sec.list.map((li, i) => (
                          <li key={i}><i className="fa fa-check-circle-o"></i> {li}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}
            </div>
          </div>

          <div className="side-blogs">
            {sidebarBlogs.map((post) => (
              <div key={post.id} className="side-blog-card">
                <img src={post.image || ""} alt={post.title} />
                <div className="side-blog-content">
                  <h4>{post.title}</h4>
                  <div className="side-blog-header">
                    <span className="blog-date">{post.date}</span>
                    <Link to={`/blog/${post.slug}`} className="blog-btn small">Read More</Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="admissions-banner mt-3 mt-md-0">
        <div className="container">
          <div className="admissions-content">
            <h2>ADMISSIONS</h2>
            <Link to="https://admission.care.edu.in/" className="apply-now-btn">APPLY NOW</Link>
          </div>
        </div>
      </div>
    </>
  );
};

export default BlogDetails;